import { Network } from 'lucide-react';
import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import type { ModuleDefinition } from '../../app/modules';
import { useMgmtAccess } from './access';
import { MgmtBadges, mgmtTaskAlerts, MgmtTaskPanel, MgmtUnitCell } from './TaskMgmt';

/**
 * Управлінський контур (М7) — управлінський шар над задачами (перенесення Управлінського порталу):
 * дерево цілей S і напрямів зусиль P, паспорт одиниці, звіти й прогнози, блокери, рішення, приймання,
 * координації, цілі й портфелі, статус-оновлення, огляди, ескалації, коло й мітки.
 * У задачі — розділ «Управління», значки й колонка «Одиниця» з task.ext.mgmt.
 */
const page = (load: () => Promise<{ default: ComponentType }>): Pick<RouteObject, 'lazy'> => ({
  lazy: async () => ({ Component: (await load()).default }),
});

const mgmtModule: ModuleDefinition = {
  key: 'mgmt',
  label: 'Управлінський контур',
  icon: Network,
  basePath: '/mgmt',
  scope: 'mgmt:read',
  useAccess: useMgmtAccess,
  order: 20,
  routes: [
    {
      ...page(() => import('./MgmtLayout')),
      children: [
        { index: true, lazy: async () => ({ Component: (await import('./MgmtLayout')).MgmtIndex }) },
        { path: 'dashboard', ...page(() => import('./pages/DashboardPage')) },
        { path: 'inbox', ...page(() => import('./pages/InboxPage')) },
        { path: 'units', ...page(() => import('./pages/UnitsPage')) },
        { path: 'units/:unitId', ...page(() => import('./pages/UnitPage')) },
        { path: 'blockers', ...page(() => import('./pages/BlockersPage')) },
        { path: 'coordination', ...page(() => import('./pages/CoordinationPage')) },
        { path: 'coordination/:coordinationId', ...page(() => import('./pages/CoordinationSnapshotPage')) },
        { path: 'goals', ...page(() => import('./pages/GoalsPage')) },
        { path: 'goals/:goalId', ...page(() => import('./pages/GoalPage')) },
        { path: 'portfolios/:portfolioId', ...page(() => import('./pages/PortfolioPage')) },
        { path: 'reviews', ...page(() => import('./pages/ReviewsPage')) },
        { path: 'calendar', ...page(() => import('./pages/CalendarPage')) },
        { path: 'escalations', ...page(() => import('./pages/EscalationsPage')) },
        { path: 'circle', ...page(() => import('./pages/CirclePage')) },
      ],
    },
  ],
  task: {
    // Управлінський шар — окрема вкладка картки задачі: виконавцю він не заважає, колу — завжди під рукою.
    panel: { title: 'Управління', icon: Network, Component: MgmtTaskPanel, tab: 'management' },
    Badges: MgmtBadges,
    alerts: mgmtTaskAlerts,
    columns: [{ key: 'unit', header: 'Одиниця', width: 96, Cell: MgmtUnitCell }],
  },
};

export default mgmtModule;
